import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Outlet, Navigate, useNavigate } from 'react-router-dom'
import { AuthenticationContext } from '../Auth/authentication.context'
import server from '../server'

const PrivateRoutes = () => {
  const { user, setUser, setToastStatus } = useContext(AuthenticationContext)
  const [sessionExpired, setSessionExpired] = useState(false)

  const navigate = useNavigate()

  // Registered in an effect and ejected on cleanup.
  //
  // This used to run in the component body, which added a fresh interceptor
  // on every render and never removed any of them. They accumulated for the
  // lifetime of the page: each response was then run through the whole chain,
  // and a single 401 called setSessionExpired and setUser once per render
  // since mount. `setUser` is a useState setter, so it is stable and this
  // registers exactly once.
  useEffect(() => {
    const interceptorId = server.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only treat 401 as session expired; network errors (e.g.
        // ERR_CONNECTION_REFUSED) have no response.
        if (error.response?.status === 401) {
          setSessionExpired(true)
          setUser(null)
        }
        return Promise.reject(error)
      }
    )

    return () => server.interceptors.response.eject(interceptorId)
  }, [setUser])

  // Announce the expiry when it happens.
  //
  // This used to live in an effect *cleanup*, which made it depend on the
  // component unmounting to fire at all — and the cleanup that ran on the
  // false -> true transition still had the previous `sessionExpired` closed
  // over, so it saw `false` and did nothing. It worked only because clearing
  // the user unmounted the route as a side effect.
  useEffect(() => {
    if (!sessionExpired) return

    setToastStatus({
      toastStatus: 'error',
      summary: 'Session Expired',
      msg: 'Your session expired, Please login again.',
    })
    navigate('/login')
  }, [sessionExpired, setToastStatus, navigate])

  return <Fragment>{user ? <Outlet /> : <Navigate to='/login' />}</Fragment>
}

export default PrivateRoutes
